import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc.js'
import timezone from 'dayjs/plugin/timezone.js'
import { getConferenceTimezone, getConferenceTimezoneLabel, CURRENT_EVENT_YEAR } from '~~/shared/utilities/schedule'
import { MONTH_ABBR } from '#shared/utilities/schedule/month-abbr'

// Client wrapper over the Find a Session SSE endpoint (Task 8): fetches
// session suggestions for schedule gaps. Mirrors useScheduleBuilder.js's
// style — no imports for $fetch/useState/useSmartEventSource, Nuxt
// auto-imports handle resolution. dayjs/getConferenceTimezone are real
// imports (not Nuxt auto-imports), matching find-gaps.js/session-time.js.
//
// Find a Session never mutates the schedule server-side (design decision 2)
// — every session lands only because the user clicked Add, which is a
// separate POST the caller of this composable is responsible for.

dayjs.extend(utc)
dayjs.extend(timezone)

// AP-style month abbreviations (4 letters, e.g. "SEPT") — matches the Figma
// header comp, not dayjs's built-in 3-letter `MMM` token.

// A single SmartEventSource lives for the lifetime of the module, mirroring
// the singleton style of the useState-backed fields below — a plain class
// instance doesn't belong inside a ref (no reactivity needed on it), but it
// still needs to be shared across every call to useFindASession() so abort()
// can reach whatever findForGap()/findAllGaps() most recently opened.
let eventSource = null

// A gap's day-only label (weekday + month/date). Parsed at local noon so a
// plain 'YYYY-MM-DD' string can never roll backward across midnight when
// read in a timezone behind UTC (the date field itself carries no zone).
const dayLabel = (dateStr) => {
    const d = dayjs(`${dateStr}T12:00:00`)
    return { day: d.format('ddd').toUpperCase(), monthDate: `${MONTH_ABBR[d.month()]} ${d.format('D')}` }
}

// A gap's start/end instant, formatted in the conference timezone.
const timeLabel = (iso) => dayjs(iso).tz(getConferenceTimezone(CURRENT_EVENT_YEAR)).format('h:mmA')

export const useFindASession = () => {
    const state = useState('find-a-session-state', () => 'idle') // 'idle' | 'prefs' | 'working' | 'results' | 'empty' | 'error'
    const progress = useState('find-a-session-progress', () => '')
    const traceId = useState('find-a-session-trace-id', () => '')
    const slots = useState('find-a-session-slots', () => [])
    const addedLimitedCapacity = useState('find-a-session-added-limited-capacity', () => [])

    // Which entry point opened the current stream. 'all' (Fill All Open Time
    // Slots) shows one card per gap — the single best suggestion for each open
    // slot. 'gap' (Fill This Time Slot) shows the ONE gap's whole bench as a
    // browsable carousel of cards, one per candidate (Asana 687667) — a large
    // gap has room for several sessions, so the user picks from all of them
    // rather than being walked through them one at a time via 👎 re-roll.
    const mode = useState('find-a-session-mode', () => 'all') // 'all' | 'gap'

    // The single carousel "cursor" — which slot's card the modal-footer thumbs
    // (and the ←/→ control) currently act on. Indexes into `slots` (and,
    // because a spent slot is always spliced out the instant its cursor runs
    // dry — see thumbDown below — `visibleCards` stays a 1:1, same-order
    // mirror of `slots`, so this index is equally valid against either array).
    const activeIndex = useState('find-a-session-active-index', () => 0)

    // One card per live slot — a slot with a spent bench (cursor has passed
    // its last candidate) contributes nothing here; thumbDown() is what
    // actually splices such a slot out of `slots`.
    const visibleCards = computed(() =>
        slots.value
            .map((s, slotIndex) => ({ gap: s.gap, session: s.candidates[s.cursor], slotIndex, added: !!s.added, thumbedUp: !!s.thumbedUp }))
            .filter((card) => !!card.session)
    )

    // Spec decision 11 — three shapes depending on how many gaps/days are
    // represented in the current slots.
    const headerLabel = computed(() => {
        const live = slots.value
        if (!live.length) return ''

        const dates = [...new Set(live.map((s) => s.gap.date))]

        if (dates.length > 1) {
            const sortedDates = [...dates].sort()
            const first = dayLabel(sortedDates[0])
            const last = dayLabel(sortedDates[sortedDates.length - 1])
            return `${first.day}, ${first.monthDate} – ${last.day}, ${last.monthDate}    ${live.length} OPEN SLOTS`
        }

        // One gap, or several gaps on the same day: earliest start → latest end.
        let earliestGap = live[0].gap
        let latestGap = live[0].gap
        for (const s of live) {
            if (dayjs(s.gap.start).isBefore(dayjs(earliestGap.start))) earliestGap = s.gap
            if (dayjs(s.gap.end).isAfter(dayjs(latestGap.end))) latestGap = s.gap
        }

        const { day, monthDate } = dayLabel(dates[0])
        const tzLabel = getConferenceTimezoneLabel(CURRENT_EVENT_YEAR)
        return `${day} • ${monthDate}    ${timeLabel(earliestGap.start)} - ${timeLabel(latestGap.end)} ${tzLabel}`
    })

    // Keeps `activeIndex` a valid index into `slots` (or 0 when `slots` is
    // empty — at that point `state` has already flipped to 'empty' and the
    // carousel/footer aren't rendered, so the exact value is moot but must
    // still never be a stale out-of-range index if something re-reads it).
    // Called after every mutation that can change `slots.length`: the
    // `complete` handler replacing `slots` wholesale, and thumbDown splicing
    // a spent slot out.
    const clampActiveIndex = () => {
        const lastIndex = slots.value.length - 1
        if (lastIndex < 0) {
            activeIndex.value = 0
        } else if (activeIndex.value > lastIndex) {
            activeIndex.value = lastIndex
        } else if (activeIndex.value < 0) {
            activeIndex.value = 0
        }
    }

    const buildUrl = (params) => {
        const search = new URLSearchParams(params)
        // Resolved lazily, here rather than at module scope — buildUrl runs from
        // findForGap/findAllGaps, outside openStream's scope.
        const { distinctId } = useScheduleAnalytics()
        const did = distinctId()
        if (did) search.set('did', did)
        // Sessions the user removed or 👎'd — the server drops them from the
        // candidate pool so it doesn't recommend them back (Asana 687632).
        const excluded = useDismissedSessions().list()
        if (excluded.length) search.set('exclude', excluded.join(','))
        return `/api/v1/schedule/suggest?${search.toString()}`
    }

    const openStream = (url) => {
        if (eventSource) {
            try { eventSource.close() } catch (e) { /* already closed */ }
        }

        state.value = 'working'
        progress.value = ''
        traceId.value = ''

        const { SmartEventSource } = useSmartEventSource()
        eventSource = new SmartEventSource(url)
        const { track } = useScheduleAnalytics()

        eventSource.addEventListener('progress', (event) => {
            try {
                const { message } = JSON.parse(event.data)
                progress.value = message || ''
            } catch (e) {
                // Ignore malformed progress payloads — not fatal to the search.
            }
        })

        // The server sends this before it does any work, so an error still has a
        // trace id to join on. The `complete` payload re-sends it (see below) —
        // same value, so the assignment is idempotent.
        eventSource.addEventListener('trace', (event) => {
            try {
                const { traceId: id } = JSON.parse(event.data)
                if (id) traceId.value = id
            } catch (e) {
                // A malformed trace payload only costs us correlation, not the request.
            }
        })

        eventSource.addEventListener('complete', (event) => {
            try {
                const payload = JSON.parse(event.data)
                // Drop gaps the engine couldn't fill. The server returns a slot
                // for EVERY open gap, including ones with no candidate, but a
                // slot with nothing to offer is not a card and must not be
                // counted. Keeping them broke two things: headerLabel counted
                // unfillable gaps ("5 OPEN SLOTS" when only one had a
                // suggestion), and thumbs-downing the last real card left the
                // empty slots behind, so `slots.length !== 0` and the modal
                // stayed on 'results' with zero cards — a blank modal instead
                // of the No Results view.
                let nextSlots = (payload?.slots || [])
                    .filter((s) => (s.candidates || []).length > 0)
                    .map((s) => ({
                        gap: s.gap,
                        candidates: s.candidates,
                        cursor: 0,
                        added: false,
                        thumbedUp: false,
                        // These candidates are re-admitted dismissals — the only
                        // things that fit this gap were sessions the user removed
                        // or 👎'd. Carried through so the UI can distinguish them
                        // from fresh picks; no visual treatment yet.
                        relaxed: !!s.relaxed,
                    }))

                // Fill This Time Slot (Asana 687667): a single large gap comes
                // back as ONE slot carrying a bench of candidates. Display each
                // candidate as its own card in the carousel — "We think you'd
                // like these" — instead of one card with 👎 re-rolling to the
                // next. Splitting the bench into single-candidate slots makes
                // every downstream consumer (visibleCards' one-card-per-slot,
                // the ←/→ control, the plural headline, 👎 removing just the
                // current card) behave exactly as it already does for 'all'
                // mode's multi-gap carousel — no special-casing anywhere else.
                if (mode.value === 'gap') {
                    nextSlots = nextSlots.flatMap((s) =>
                        s.candidates.map((candidate) => ({
                            gap: s.gap,
                            candidates: [candidate],
                            cursor: 0,
                            added: false,
                            thumbedUp: false,
                            relaxed: s.relaxed,
                        })),
                    )
                }
                // Never clobber a good id from the `trace` event with an empty one —
                // a `complete` that omitted it would otherwise strip the only thing
                // linking these outcome events to their $ai_generation rows.
                traceId.value = payload?.traceId || traceId.value
                slots.value = nextSlots
                clampActiveIndex()
                // "No sessions match your preferences" is only true when the
                // engine actually returned zero candidates everywhere — never
                // conflate this with a request that failed (see the `error`
                // listener below).
                state.value = nextSlots.length > 0 ? 'results' : 'empty'
                track('schedule assistant suggestions shown', {
                    feature: 'find-a-session',
                    trace_id: traceId.value,
                    result_count: nextSlots.length,
                })
            } catch (e) {
                // A malformed/unreadable `complete` payload. This used to flip the
                // UI to the error screen silently — no event at all — so these were
                // invisible in analytics and indistinguishable from a user simply
                // abandoning the modal. Tagged `complete-parse` to separate it from
                // a server-rejected or dropped-connection failure.
                state.value = 'error'
                track('schedule assistant error shown', {
                    feature: 'find-a-session',
                    trace_id: traceId.value,
                    source: 'complete-parse',
                    reason: e?.message || '',
                })
            }
        })

        // `state = 'error'` here, NEVER `'empty'` — showing "no sessions
        // match your preferences" when the request actually failed is a lie
        // the UI would tell on the user's behalf. The stream must also be
        // closed BEFORE flipping state — SmartEventSource responds to an
        // error by falling back to a 2-second polling loop against
        // /api/v2/assistants/history, which suppresses the server's `done`
        // event forever; leaving eventSource open here means that poll loop
        // survives the modal closing and never stops until the next
        // openStream() or a page reload.
        eventSource.addEventListener('error', (event) => {
            // The server sends { message, statusCode, traceId } on a rejected
            // request. Only traceId used to be read, so every failure logged
            // identically and a 400 ("That time slot is not open" — a client/server
            // disagreement about the gap) was indistinguishable from a 500 or a
            // dropped connection. Keep the status and message: they're fixed
            // server-authored strings, never user content.
            let statusCode = null
            let reason = ''
            try {
                const payload = JSON.parse(event?.data || '{}')
                if (payload.traceId) traceId.value = payload.traceId
                statusCode = payload.statusCode ?? null
                reason = payload.message || ''
            } catch (e) {
                // SmartEventSource also dispatches a bare transport-level `error`
                // with no data — nothing to parse, and that's fine.
            }
            eventSource?.close()
            eventSource = null
            state.value = 'error'
            track('schedule assistant error shown', {
                feature: 'find-a-session',
                trace_id: traceId.value,
                status_code: statusCode,
                reason,
                // Keyed off the presence of a payload, not of a status code: the
                // server always sends one, so no data at all means the stream died
                // in transport rather than being rejected — a different bug class.
                source: event?.data ? 'server' : 'transport',
            })
        })

        eventSource.addEventListener('done', () => {
            eventSource?.close()
            eventSource = null
        })

        return eventSource
    }

    // Opens the SSE stream for a single gap (Fill This Time Slot). 'gap' mode
    // fans the returned bench out into one card per candidate (see `complete`).
    const findForGap = (gap) => {
        mode.value = 'gap'
        const params = { date: gap.date, start: gap.start, end: gap.end }
        // A time-range window (Case 2 empty state) is an open span the user chose,
        // not a gap bounded by existing sessions — flag it so the server suggests
        // sessions that fit the window instead of requiring an exact-gap match.
        if (gap.windowed) params.window = '1'
        // Only meaningful with `window`: the server drops this session from the
        // busy intervals so the slot it currently occupies counts as free.
        if (gap.replacing) params.replacing = gap.replacing
        return openStream(buildUrl(params))
    }

    // Opens the SSE stream for every open gap across all days (Fill All Open Time Slots).
    const findAllGaps = () => {
        mode.value = 'all'
        return openStream(buildUrl({ all: '1' }))
    }

    // 👍 — locks a positive sentiment onto the slot's model (the button then
    // renders selected and cannot be re-triggered). Returns true only on the
    // transition from un-thumbed → thumbed so the caller fires the PostHog
    // signal exactly once; a repeat click on an already-locked card is a no-op.
    const thumbUp = (slotIndex) => {
        const slot = slots.value[slotIndex]
        if (!slot || slot.thumbedUp) return false
        slot.thumbedUp = true
        return true
    }

    // 👎 re-rolls: advance that slot's cursor only. Once the cursor passes
    // the last candidate, the slot is spliced out; if that was the last
    // slot, the carousel is empty and the whole flow flips to 'empty'.
    const thumbDown = (slotIndex) => {
        const slot = slots.value[slotIndex]
        if (!slot) return

        // Remember the rejected session so it isn't recommended back on a later
        // Find a Session or full build (Asana 687632). Captured before the cursor
        // advances, while it still points at the card the user thumbed down.
        const rejected = slot.candidates[slot.cursor]
        if (rejected?.unique_id) useDismissedSessions().dismiss(rejected.unique_id)

        slot.cursor += 1
        // The re-rolled candidate is a fresh card — it must not inherit the
        // previous session's thumbs-up lock.
        slot.thumbedUp = false
        if (slot.cursor >= slot.candidates.length) {
            slots.value.splice(slotIndex, 1)
            if (slots.value.length === 0) state.value = 'empty'
            clampActiveIndex()
        }
    }

    // Moves the carousel cursor to slot/card `i`, clamped into
    // [0, slots.length - 1] (or 0 when `slots` is empty).
    const setActive = (i) => {
        const lastIndex = slots.value.length - 1
        activeIndex.value = lastIndex < 0 ? 0 : Math.max(0, Math.min(i, lastIndex))
    }

    // ←/→ control — one step at a time, clamped at both ends (no wraparound).
    const next = () => setActive(activeIndex.value + 1)
    const prev = () => setActive(activeIndex.value - 1)

    // Flags a slot as added (the card flips to an "Added" state) without
    // removing it — the carousel keeps its length (decision 15).
    const markAdded = (slotIndex) => {
        const slot = slots.value[slotIndex]
        if (!slot) return

        slot.added = true

        const session = slot.candidates[slot.cursor]
        if (session?.session_reservations === true) {
            addedLimitedCapacity.value.push(session)
        }
    }

    // Closes the stream in flight (if any) and returns to idle, without
    // clearing anything already fetched.
    const abort = () => {
        if (eventSource) {
            try { eventSource.close() } catch (e) { /* already closed */ }
            eventSource = null
        }
        state.value = 'idle'
    }

    // Fully clears the flow — used when the modal is dismissed for good and
    // the next open should start fresh.
    const reset = () => {
        abort()
        progress.value = ''
        traceId.value = ''
        slots.value = []
        addedLimitedCapacity.value = []
        activeIndex.value = 0
    }

    return {
        state,
        progress,
        traceId,
        slots,
        activeIndex,
        visibleCards,
        headerLabel,
        addedLimitedCapacity,
        findForGap,
        findAllGaps,
        thumbUp,
        thumbDown,
        markAdded,
        setActive,
        next,
        prev,
        abort,
        reset,
    }
}
